import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../hooks/apiClient';

const tg = window.Telegram?.WebApp;

const STATUS = {
  pending: { title: 'Пачки продаются', className: 'bl-chip-wait' },
  confirmed: { title: 'Завершён', className: 'bl-chip-ok' },
  rejected: { title: 'Отклонён', className: 'bl-chip-err' },
  expired: { title: 'Просрочен', className: 'bl-chip-err' },
};

const formatDate = (iso) =>
  new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

const plural = (n, forms) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
};

const Balance = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    tg?.ready();
    tg?.expand();
    apiClient
      .get('/v1/me/balance')
      .then((response) => setData(response.data))
      .catch((e) => {
        const detail = e.response?.data?.detail;
        setError(typeof detail === 'string' && e.response?.status !== 401
          ? detail
          : 'Не удалось загрузить баланс. Откройте его из бота');
      });
  }, []);

  return (
    <div className="bl-root">
      <style>{styles}</style>
      <div className="bl-header">
        <h1 className="bl-title">Баланс</h1>
        <button className="bl-scan" onClick={() => navigate('/scan_qr')}>📷 Сканировать</button>
      </div>

      {error && <p className="bl-error">{error}</p>}
      {!data && !error && <div className="bl-spinner" />}

      {data && (
        <>
          <div className="bl-card">
            <div className="bl-points">{data.points}</div>
            <div className="bl-caption">{plural(data.points, ['балл', 'балла', 'баллов'])} на балансе</div>
            {data.pending.count > 0 && (
              <div className="bl-pending">
                ⏳ Ждут вывода из оборота: {data.pending.count}{' '}
                {plural(data.pending.count, ['пачка', 'пачки', 'пачек'])} (до +{data.pending.points})
              </div>
            )}
          </div>

          <h2 className="bl-section">История сканов</h2>
          {data.scans.length === 0 && <p className="bl-empty">Сканов пока нет</p>}
          {data.scans.map((scan, i) => {
            const status = STATUS[scan.status] || { title: scan.status, className: '' };
            return (
              <div className="bl-row" key={`s${i}`}>
                <div className="bl-row-main">
                  <div className="bl-row-title">{scan.product}</div>
                  <div className="bl-row-sub">
                    {formatDate(scan.created_at)} · <span className={`bl-chip ${status.className}`}>{status.title}</span>
                    {scan.promotion && (
                      <span className="bl-chip bl-chip-promo" title={scan.promotion}>🎁 акция {scan.multiplier}</span>
                    )}
                  </div>
                  {scan.packs_total > 0 && (
                    <div className="bl-row-sub">Продано пачек: {scan.packs_sold} из {scan.packs_total}</div>
                  )}
                  {scan.reason && <div className="bl-row-reason">{scan.reason}</div>}
                </div>
                <div className={`bl-amount${scan.points > 0 ? ' bl-amount-plus' : ''}`}>
                  {scan.points > 0 ? `+${scan.points}` : scan.status === 'pending' ? '…' : '0'}
                </div>
              </div>
            );
          })}

          {data.operations.length > 0 && (
            <>
              <h2 className="bl-section">Другие операции</h2>
              {data.operations.map((op, i) => (
                <div className="bl-row" key={`o${i}`}>
                  <div className="bl-row-main">
                    <div className="bl-row-title">{op.title}</div>
                    <div className="bl-row-sub">{formatDate(op.created_at)}</div>
                  </div>
                  <div className={`bl-amount${op.amount > 0 ? ' bl-amount-plus' : ' bl-amount-minus'}`}>
                    {op.amount > 0 ? `+${op.amount}` : op.amount}
                  </div>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
};

const styles = `
  .bl-root {
    min-height: 100vh;
    padding: 16px 16px calc(24px + env(safe-area-inset-bottom));
    box-sizing: border-box;
    background: var(--tg-theme-bg-color, #17212b);
    color: var(--tg-theme-text-color, #f5f5f5);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  .bl-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
  .bl-title { font-size: 24px; font-weight: 700; margin: 0; }
  .bl-scan {
    padding: 10px 14px; border: none; border-radius: 12px;
    background: #10b981; color: #fff; font-size: 14px; font-weight: 600; cursor: pointer;
  }
  .bl-card {
    padding: 20px; border-radius: 18px; text-align: center; margin-bottom: 24px;
    background: var(--tg-theme-secondary-bg-color, #232e3c);
  }
  .bl-points { font-size: 48px; font-weight: 800; line-height: 1.1; color: #10b981; }
  .bl-caption { font-size: 14px; color: var(--tg-theme-hint-color, #8b98a5); margin-top: 4px; }
  .bl-pending {
    margin-top: 14px; padding: 10px 12px; border-radius: 12px;
    background: rgba(245, 158, 11, 0.15); color: #fbbf24; font-size: 14px;
  }
  .bl-section {
    font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em;
    color: var(--tg-theme-hint-color, #8b98a5); margin: 20px 0 8px;
  }
  .bl-row {
    display: flex; align-items: center; gap: 12px; padding: 12px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }
  .bl-row-main { flex: 1; min-width: 0; }
  .bl-row-title { font-size: 15px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .bl-row-sub { font-size: 12px; color: var(--tg-theme-hint-color, #8b98a5); margin-top: 3px; }
  .bl-row-reason { font-size: 12px; color: #f87171; margin-top: 3px; }
  .bl-chip { padding: 1px 6px; border-radius: 6px; font-size: 11px; }
  .bl-chip-ok { background: rgba(16, 185, 129, 0.15); color: #34d399; }
  .bl-chip-wait { background: rgba(245, 158, 11, 0.15); color: #fbbf24; }
  .bl-chip-err { background: rgba(239, 68, 68, 0.15); color: #f87171; }
  .bl-chip-promo { margin-left: 4px; background: rgba(168, 85, 247, 0.18); color: #c084fc; }
  .bl-amount { font-size: 16px; font-weight: 700; color: var(--tg-theme-hint-color, #8b98a5); }
  .bl-amount-plus { color: #34d399; }
  .bl-amount-minus { color: #f87171; }
  .bl-empty, .bl-error { text-align: center; color: var(--tg-theme-hint-color, #8b98a5); margin: 24px 0; }
  .bl-error { color: #f87171; }
  .bl-spinner {
    width: 36px; height: 36px; margin: 48px auto;
    border: 4px solid rgba(255, 255, 255, 0.2); border-top-color: #10b981; border-radius: 50%;
    animation: bl-spin 0.8s linear infinite;
  }
  @keyframes bl-spin { to { transform: rotate(360deg); } }
`;

export default Balance;
